import {
  DropdownField,
  DropdownOptionItem,
  FieldTypes,
  InputTemplateAttachment,
  isDropdownOptionItem,
} from '@hb/shared'
import { useCallback, useMemo } from 'react'
import { DEFAULT_RADIO_FONT_SIZE } from '../../PdfView/attachmentFieldStyles'
import { useExternalPdfTemplateEditor } from '../../Templates/ExternalPdf/context'
import { FontSizeSelect } from '../ToolbarComponents'
import { RadioFieldToolbarControls } from './RadioFieldToolbarControls'

// Post-placement editor for a selected radio (DROPDOWN) input attachment. Mirrors AttachmentStyleToolbar:
// reads the selection from context and writes the attachment back via updateAttachment. Direction/options
// live on the field; font size lives on the attachment (it drives the resized box's rendered input size).
export const RadioInputToolbar: React.FC = () => {
  const { selectedAttachmentId, externalPdf, updateAttachment } = useExternalPdfTemplateEditor()

  const attachment = selectedAttachmentId ? externalPdf.attachments[selectedAttachmentId] : null
  const inputAttachment =
    attachment?.type === 'input' && attachment.field.type === FieldTypes.DROPDOWN
      ? attachment
      : null
  const field = inputAttachment?.field as DropdownField | undefined

  const options = useMemo<DropdownOptionItem[]>(
    () => (field?.options ?? []).filter(isDropdownOptionItem),
    [field],
  )
  const direction = field?.radioDirection ?? 'row'
  const fontSize = inputAttachment?.fontSize ?? DEFAULT_RADIO_FONT_SIZE

  const patchAttachment = useCallback(
    (patch: Partial<InputTemplateAttachment>) => {
      if (!selectedAttachmentId || !inputAttachment) return
      updateAttachment(selectedAttachmentId, { ...inputAttachment, ...patch })
    },
    [selectedAttachmentId, inputAttachment, updateAttachment],
  )

  const updateField = useCallback(
    (patch: Partial<DropdownField>) => {
      if (!field) return
      patchAttachment({ field: { ...field, ...patch } })
    },
    [field, patchAttachment],
  )

  if (!inputAttachment || !field) return null

  return (
    <RadioFieldToolbarControls
      direction={direction}
      onDirectionChange={radioDirection => updateField({ radioDirection })}
      options={options}
      onOptionsChange={newOptions => updateField({ options: newOptions })}>
      <FontSizeSelect
        fontSize={fontSize}
        setFontSize={size => patchAttachment({ fontSize: size })}
        step={0.1}
      />
    </RadioFieldToolbarControls>
  )
}
